import { Form } from 'antd';
import classNames from 'classnames';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import Vditor from 'vditor';
import getFormatMessage from '../../../locales/get-format-message';
import '../style.vditor.scss';

const MAX_LENGTH = 2000;

interface IProps {
  readonly initValue?: string;
  readonly id: string;
  readonly label: string;
  readonly name: string;
  readonly errorMessage?: string;
  readonly placeholder?: string;
  readonly contentExample?: string;
  readonly disabled?: boolean;
}

export const VDitorField = forwardRef((props: IProps, ref) => {
  const { disabled } = props;
  const [errMsg, setErrMsg] = useState<string>(props.errorMessage || '');
  const vdInstance = useRef<Vditor>();

  useImperativeHandle(ref, () => ({
    validate,
    getValue,
  }));

  useEffect(() => {
    if (disabled) {
      const container = document.querySelector(`#${props.id}`) as
        | HTMLElement
        | undefined;
      if (!container || !props.initValue) {
        return;
      }
      container.innerHTML = props.initValue;
      return;
    }
    const vditor = new Vditor(props.id, {
      height: 200,
      lang: 'en_US',
      placeholder: props.placeholder,
      toolbar: [
        'headings',
        'bold',
        'italic',
        'strike',
        'link',
        '|',
        'list',
        'ordered-list',
        'check',
        'outdent',
        'indent',
        '|',
        'quote',
        'table',
        '|',
        'undo',
        'redo',
        '|',
        'edit-mode',
      ],
      mode: 'wysiwyg',
      input: onVdInput,
      after: () => {
        vdInstance.current = vditor;
        if (props.initValue) {
          const mdContent = vditor.html2md(props.initValue) || '';
          vditor.setValue(mdContent);
          return;
        }

        vditor.setValue(props.contentExample || '');
      },
    });
  }, []);

  const getValue = () => {
    return vdInstance.current?.getHTML() || '';
  };

  const validate = (inputValue?: string) => {
    const value = inputValue || vdInstance.current?.getHTML() || '';

    if (!value?.trim()) {
      setErrMsg(props.placeholder || getFormatMessage('please_update_content'));
      return false;
    }

    if (value?.trim().length > MAX_LENGTH) {
      setErrMsg(
        getFormatMessage('exceed_character_limit', {
          params: {
            maxLength: MAX_LENGTH,
          },
        })
      );
      return false;
    }
    setErrMsg('');
    return true;
  };

  const onVdInput = (value: string) => {
    validate(value);
  };

  return (
    <Form.Item
      label={props.label}
      name="cnContent"
      rules={[
        {
          required: true,
          message: '',
          validator: () => Promise.resolve(true),
        },
      ]}
    >
      <div
        id={props.id}
        className={classNames('vditor', errMsg ? 'error' : '')}
      />
      {errMsg ? (
        <div
          id="basic_Content_help"
          className="ant-form-item-explain ant-form-item-explain-connected"
          role="alert"
        >
          <div className="ant-form-item-explain-error">{errMsg}</div>
        </div>
      ) : null}
    </Form.Item>
  );
});
